import { Flex, Form } from 'antd';
import { GeneralSection, LocationSection, PasswordSection, PhoneSection } from 'features/auth/registration';
import { ProgressHeader } from '../components/ProgressHeader';
import { GridContainer } from 'shared/ui';
import { createElement, FunctionComponent, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetCountriesQuery } from 'entities/countries/api';
import { Country } from 'entities/countries/model/types';
import { general, generalFields } from 'shared/model/schemas/rules';
import { Rule } from 'antd/es/form';
import { SignUpFormData } from '../model/types';
import { useDispatch } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useCreateAccountMutation } from '../api';
import { ApiError } from 'shared/model/types';
import moment from 'moment';
import { useMessage } from 'shared/libs/hooks';
import { ROUTES } from 'shared/const/routes';
import { useNavigate } from 'react-router';
import { GTAG_ID } from 'shared/const/app';
import { useSaveGtagMutation } from 'entities/user';
import classes from './RegistrationPage.module.css';

type Sections = {
  title: string;
  description: string;
  content: FunctionComponent<{ countries: Country[], formRules: Record<string, Rule[]>, formData: Record<string, unknown> }>
}

export const RegistrationPage = () => {
  const [current, setCurrent] = useState(0);
  const [formData, setFormData] = useState<Partial<SignUpFormData>>({});
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: countriesResponse } = useGetCountriesQuery({ enroll: true });
  const countries = countriesResponse?.response ?? [];
  const formRules: Record<string, Rule[]> = {
    firstName: [general(t)['required-field'], ...generalFields(t).name],
    lastName: [general(t)['required-field'], ...generalFields(t).surname],
    gender: [general(t)['required-field']],
    email: [general(t)['required-field'], ...generalFields(t).email],
    dateOfBirth: [general(t)['required-field']],
    country: [general(t)['required-field']],
    city: [general(t)['required-field'], ...generalFields(t).city],
    address: [general(t)['required-field'], ...generalFields(t).address],
    postalCode: [general(t)['required-field'], ...generalFields(t).postalCode],
    password: [general(t)['required-field'], ...generalFields(t).password],
  };
  const [createAccount] = useCreateAccountMutation();
  const [saveGtag] = useSaveGtagMutation();
  
  const { showError, showSuccess } = useMessage();

  const sections = useMemo<Sections[]>(() => [
    {
      title: t({ id: 'screens.auth.signup.private.title' }),
      description: t({ id: 'screens.auth.signup.private.legend' }),
      content: GeneralSection
    },
    {
      title: t({ id: 'screens.auth.signup.private.title' }),
      description: t({ id: 'screens.auth.signup.private.legend' }),
      content: LocationSection
    },
    {
      title: t({ id: 'screens.auth.phone.title' }),
      description: t({ id: 'screens.createAccount.subTitle' }),
      content: PhoneSection
    },
    {
      title: t({ id: 'screens.setting.setting.setting.nav.password' }),
      description: t({ id: 'password.hint' }),
      content: PasswordSection
    },
  ], [t]
  );

  const onBack = () => {
    setCurrent((prev) => prev - 1);
  }

  const onNext = () => {
    setCurrent((prev) => prev + 1);
  }

  const handleSubmit = (values: Partial<SignUpFormData>) => {
    //@ts-ignore
    window.grecaptcha.ready(function () {
      //@ts-ignore
      window.grecaptcha.execute(process.env.REACT_APP_CAPTCHA_KEY, { action: 'submit' })
        .then((token: string) => {
          dispatch(setActivity(true));
          createAccount({
            address: {
              city: values?.city,
              country: values?.country,
              line1: values?.address,
              line2: '',
              postalCode: values?.postalCode
            },
            dateOfBirth: moment(Object.values(values?.dateOfBirth ?? '01-01-1999').join('/'), 'DD/MM/YYYY').format('DD-MM-YYYY'),
            email: values?.email,
            firstName: values?.firstName,
            'g-recaptcha-response': token,
            gender: values?.gender,
            language: 'EN',
            lastName: values?.lastName,
            password: values?.password,
            phone: values.phone?.phoneNumber,
            phoneCountry: values.phone?.countryCode,
            privacyPolicy: values?.privacyPolicy,
            termsAndConditions: values?.termsAndConditions,
            utm: {},
          })
            .unwrap()
            .then(async ({ response }) => {
              showSuccess?.(t({ id: 'river.user.account.createRiverAccount.success' }));
              
              gtag('get', GTAG_ID, 'client_id', (clientID) => {
                saveGtag({ clientId: clientID as string, eventName: 'web_regustration_successful', gtag: GTAG_ID });
                gtag('event', 'web_regustration_successful', {
                  user_id: response.id,
                  email: response.email,
                });
              });
              navigate({ pathname: ROUTES.CONFIRM_EMAIL.path, search: `email=${response.email}&phone=${response.phone}` },);
            }).catch((error: ApiError) => {
              const apiErrors = error?.data?.errors;

              if (apiErrors && apiErrors.length > 0) {
                apiErrors.forEach((e) => {
                  showError(
                    t({ id: e.id, defaultMessage: e.defaultMessage })
                  );
                });

                const phoneError = apiErrors.find((e) => e.field === 'phone');
                const emailError = apiErrors.find((e) => e.field === 'email');
                const dobError = apiErrors.find((e) => e.field === 'dateOfBirth');

                if (phoneError) {
                  setCurrent(2);
                } else if (emailError) {
                  setCurrent(0);
                } else if (dobError) {
                  setCurrent(0);
                }
              } else {
                showError(t({ id: 'common.message.error' }));
              }
            })
            .finally(() => {
              dispatch(setActivity(false));
            })
        })
    })
  }


  return (
    <GridContainer>
      <div className={classes.container}>
        <Flex vertical gap={40} style={{ width: '100%' }} className={classes.contentWrapper}>
          <ProgressHeader title={sections[current].title} description={sections[current].description} sectionIndex={current} onBack={onBack} last={sections.length + 2} />
          <Form.Provider
            onFormFinish={(name, info) => {
              setFormData((prev) => {
                const updated = { ...prev, ...info.values };
                if (name === 'password') {
                  handleSubmit(updated);
                }
                return updated;
              });
              if (name !== 'password') {
                onNext()
              }
            }}
            onFormChange={(name, { changedFields, forms }) => {
              if (name === 'password' && changedFields && changedFields.length > 0) {
                const passwordForm = forms?.password;
                if (passwordForm) {
                  const allValues = passwordForm.getFieldsValue();
                  setFormData((prev) => ({ ...prev, ...allValues }));
                }
              }
            }}
          >
            {createElement(sections[current].content, { countries, formRules, formData })}
          </Form.Provider>
        </Flex>
      </div>
    </GridContainer>
  )
}